import React, { useState, useMemo, useRef, useEffect } from "react"
import { useParams } from "react-router-dom"
import { useStateWithCallbackLazy } from "use-state-with-callback"
import dayjs from "dayjs"

// components
import AssessmentsGrid from "./AssessmentsGrid/AssessmentsGrid"
import CreateActivityModal from "./CreateActivityModal/CreateActivityModal"
import { PrimeButton } from "components-design-system"
import { Tooltip, Flex, Dropdown } from "antd"
import { Panel } from "components-design-system"

// context
import useAppState from "context/hooks/useAppState"

// constants
import { resultsFiltersMap, resultsFilterLabels } from "constants/assessmentResults"
import { ASSESSMENT_TASKS } from "constants/tasks"
// apis
import { useUserPermissions, useUserSettings } from "api/hooks"
import { useGetStudy } from "api/hooks/studies/useStudiesApi"
import { useGetStudyAssessments, usePostRunAllAssessments } from "api/hooks/studies/useAssessmentsApi"

// styles
import "./Assessments.scss"

const Assessments = ({ setMissingFields = null }) => {
  const { studyId } = useParams()
  const gridRef = useRef()

  // local state
  const [showDeactivateTrees, setShowDeactivatedTrees] = useStateWithCallbackLazy(false)
  // resultFilters used for active filter button states and to calculate adding/subtracting from current state
  const [resultFilters, setResultFilters] = useStateWithCallbackLazy([
    "In Scope",
    "Insufficient Data",
    "Mandatory",
    "Mandatory (Manual)",
    // "Not Required",
    // "Not Required (Manual)"
    "Not Yet Assessed",
    "Opted In",
    "Optional",
    "Voluntary",
  ])
  const [showCreateActivityModal, setShowCreateActivityModal] = useState(false)
  const [selectedAssessment, setSelectedAssessment] = useState(null)

  // server state
  const { data: assessments, refetch: refetchAssessments } = useGetStudyAssessments(studyId, showDeactivateTrees)
  const postRunAllAssessments = usePostRunAllAssessments(studyId)
  const { data: permissions } = useUserPermissions()
  const { data: study } = useGetStudy(studyId)
  const { data: userSettings } = useUserSettings()
  const dateFormat = userSettings?.date_format

  const toggleRefetchAssessments = () => {
    setShowDeactivatedTrees(
      (bool) => !bool,
      () => {
        refetchAssessments()
      }
    )
  }

  const { pollingTaskId, setPollingTaskId, setTaskType, taskType, pollingCallbackRef } = useAppState()

  useEffect(() => {
    if (!pollingTaskId) {
      refetchAssessments()
    }
  }, [pollingTaskId])

  // utility to find all filler values for a key
  const getResultValuesByKey = (key) => {
    // find unique values from assessments
    const allDataResults = [...assessments].map((dataItem) => dataItem.result)
    const uniqueDataResults = [...new Set(allDataResults)]

    let keyValues = []
    // all values in data
    if (key === "all") {
      keyValues = uniqueDataResults
    }
    // values in data associated to key
    else {
      keyValues =
        Object.keys(resultsFiltersMap).filter(
          (mapVal) => key === resultsFiltersMap[mapVal] && uniqueDataResults.find((dataVal) => dataVal === mapVal)
        ) || []
    }
    return keyValues
  }

  const isFilterActive = (key, filterValues) => {
    if (filterValues === null) return true

    // find all values in resultsFiltersMap for a key
    const keyValues = getResultValuesByKey(key)

    // if key value not in data
    if (keyValues?.length === 0) return false

    // check if all values for key are in current filter
    const everyKeyInFilters = keyValues.every((keyVal) => {
      if (filterValues) return filterValues.includes(keyVal)
      return true
    })

    if (everyKeyInFilters) return true
    return false
  }

  const filterByResult = (key) => {
    const gridApi = gridRef.current.api

    // find all values in resultsFiltersMap for a key
    const keyValues = getResultValuesByKey(key)

    // check if all values for key are in current filter
    const everyKeyInFilters = keyValues.every((keyVal) => {
      if (resultFilters) return resultFilters.includes(keyVal)
      return true
    })

    // add or subtract from current filter
    // null means all rows showing; [] means no rows showing
    let newFilter = []
    if (key === "all") {
      // remove all rows
      if (!resultFilters || everyKeyInFilters) {
        newFilter = []
      }
      // add all rows
      else {
        // also if resultFilters is []
        newFilter = null
      }
    } else {
      let filterState = resultFilters

      // when resultFilters null, filtered to all items
      if (!resultFilters) {
        // find unique values from assessments
        filterState = getResultValuesByKey("all")
      }

      // remove key items from filter
      if (everyKeyInFilters) {
        newFilter = filterState.filter((filter) => !keyValues.includes(filter))
      }
      // add key item(s) to filter
      else {
        newFilter = [...new Set([...filterState, ...keyValues])]
      }
    }

    setResultFilters(newFilter, () => {
      gridApi.setColumnFilterModel("result", { values: newFilter }).then(() => {
        gridApi.onFilterChanged()
      })
    })
  }

  const renderFilterButtons = useMemo(() => {
    const getCount = (filterKey) => {
      if (filterKey === "all") return assessments.length
      let count = 0
      assessments.forEach((assess) => {
        if (filterKey === resultsFiltersMap[assess.result]) {
          count++
        }
      })
      return count
    }

    if (assessments?.length > 0) {
      return Object.keys(resultsFilterLabels).map((filterKey) => {
        const label = resultsFilterLabels[filterKey]
        const count = getCount(filterKey)
        // if no key value in data, not active:
        const activeState = count === 0 ? false : isFilterActive(filterKey, resultFilters)
        return (
          <Tooltip
            title={`${count} assessments`}
            key={filterKey}
          >
            <div
              className={`filter-checkbox-group${count === 0 ? " disabled" : ""}`}
              onClick={count === 0 ? null : () => filterByResult(filterKey)}
            >
              <span className={`filter-checkbox${filterKey ? " " + filterKey : ""}${activeState ? " active" : ""}`}>
                &nbsp;
              </span>
              <span className="filter-label">{label}</span>
            </div>
          </Tooltip>
        )
      })
    }
    return null
  }, [assessments, resultFilters])

  const toggleCreateActivityModal = (assessment) => {
    if (assessment) {
      setSelectedAssessment(assessment)
      setShowCreateActivityModal(true)
    } else {
      setSelectedAssessment(null)
      setShowCreateActivityModal(false)
    }
  }

  const onCreatedActivities = () => {
    setShowCreateActivityModal(false)
  }

  const onGridReady = ({ api }) => {
    api.setFilterModel({
      result: {
        filterType: "set",
        values: resultFilters,
      },
    })
  }

  // when changing filters from grid, update filter button states
  const onFilterChanged = ({ api, source, columns }) => {
    if (source === "columnFilter") {
      const filterInstance = api.getFilterInstance("result")
      const filterValues = filterInstance.getModel()?.values || null
      const filterModel = api.getFilterModel()
      setResultFilters(filterValues, () => {
        api.setFilterModel(filterModel)
      })
    }
  }

  const exportGrid = () => {
    gridRef.current.api.exportDataAsCsv({
      fileName: `prime-${study.study_id}-assessments-${dayjs().format(dateFormat)}`,
      processCellCallback(params) {
        const value = params.value
        if (params.column.colId === "attached_projects") {
          return params.node.data.attached_projects.map((proj) => proj.title)
        }

        return value
      },
    })
  }

  const actions = React.useMemo(() => {
    const items = [
      <Flex
        className="filter-buttons-container"
        gap={11}
      >
        {renderFilterButtons}
      </Flex>,
    ]
    if (permissions?.["assessment.edit"]) {
      items.push(
        <PrimeButton
          type="primary"
          style={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
          }}
          onClick={() =>
            postRunAllAssessments(
              {},
              {
                onSuccess: (resp) => {
                  setPollingTaskId(resp?.data?.task_id)
                  setTaskType(ASSESSMENT_TASKS.runAllAssessments)
                  pollingCallbackRef.current = (pollingData) => {
                    refetchAssessments()
                  }
                },
              }
            )
          }
          loading={taskType === ASSESSMENT_TASKS.runAllAssessments}
        >
          {taskType === ASSESSMENT_TASKS.runAllAssessments ? `Running assessments` : "Run all assessments"}
        </PrimeButton>
      )
    }

    items.push(
      <Dropdown
        placement="bottomRight"
        menu={{
          items: [
            {
              label: "Export",
              onClick: () => exportGrid(),
            },
            {
              label: `${showDeactivateTrees ? "Hide" : "Show"} deactivated assessments`,
              onClick: () => toggleRefetchAssessments(),
            },
          ],
        }}
        overlayStyle={{ width: 154 }}
      >
        <PrimeButton icon={<i className="fal fa-ellipsis-v" />} />
      </Dropdown>
    )
    return items
  }, [permissions, showDeactivateTrees, renderFilterButtons])

  return (
    <Panel
      title="ASSESSMENTS"
      actions={actions}
      iconClassName="fa-light fa-diagram-nested"
      className="assessments-page"
    >
      <AssessmentsGrid
        gridRef={gridRef}
        rowData={assessments}
        onFilterChanged={onFilterChanged}
        onGridReady={onGridReady}
        lockPosition={false}
        permissions={permissions}
        toggleCreateActivityModal={toggleCreateActivityModal}
        setMissingFields={setMissingFields}
      />
      {permissions && permissions["project.create"] === true && (
        <CreateActivityModal
          open={showCreateActivityModal}
          onCancel={() => setShowCreateActivityModal(false)}
          assessment={selectedAssessment}
          assessments={assessments}
          onCreated={onCreatedActivities}
        />
      )}
    </Panel>
  )
}

export default Assessments
